import {
  forwardRef,
  type ButtonHTMLAttributes,
} from 'react'
import { Icon, type IconName } from '@components/atoms/Icon'
import { Loader } from './Loader'
import styles from './ButtonUtility.module.scss'

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Square icon-only button. Hierarchy mirrors `Buttons/Button utility` from
 * the Catalyst Mobile library (Figma node `8286:3756`).
 *
 * Never nest inside another `<button>`, `<a href>`, or other interactive
 * control — use `<Icon>` or a styled `<span aria-hidden="true">` for
 * decorative affordances inside larger tap targets.
 *
 *   - `'primary'`      → brand fill (green) + 1 px translucent inner border;
 *     rarely used — reserve for iconic primary actions like the FAB "+"
 *   - `'secondary'`    → white surface + 1 px border + skeu shadow
 *   - `'tertiary'`     → transparent
 *   - `'tertiary-alt'` → subtle gray pill that **consumes the tertiary
 *     token family** (background hooks into
 *     `--colors-background-gray-tertiary`). Use when the surrounding
 *     surface theming should drive the pill colour automatically across
 *     light and dark modes.
 *   - `'quaternary'`   → subtle gray pill that **consumes the dedicated
 *     `--component-buttons-quaternary-*` tokens** and matches the
 *     Catalyst Mobile `Quaternary` Figma dimension literally (including
 *     the focused-state background flip to `--colors-background-gray-primary`
 *     and a transparent disabled background). Use when the design
 *     explicitly references Quaternary.
 */
export type ButtonUtilityType =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'tertiary-alt'
  | 'quaternary'

/**
 * Square container size.
 *   - `'sm'` → 32 × 32 px, radius 8 (md), icon 20 px
 *   - `'md'` → 40 × 40 px, radius 12 (xl), icon 20 px
 *   - `'lg'` → 48 × 48 px, radius 16 (2xl), icon 20 px
 *
 * Padding stays at 6 px for every size, so the icon is centred with
 * progressively more whitespace as the container grows.
 */
export type ButtonUtilitySize = 'sm' | 'md' | 'lg'

type NativeButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'type' | 'children'
>

export interface ButtonUtilityProps extends NativeButtonProps {
  /** Visual variant. Defaults to `'tertiary'`. */
  type?: ButtonUtilityType
  /** Container size. Defaults to `'sm'`. */
  size?: ButtonUtilitySize
  /** Switches the colour family to the error/destructive ramp. */
  destructive?: boolean
  /** Icon name (from the registry). Required — this button has no label. */
  icon: IconName
  /**
   * Loading state. Replaces the icon with a centred spinner and announces
   * `aria-busy=true`. The button stays the same square size.
   */
  loading?: boolean
  /** Native `<button>` semantic — defaults to `'button'`. */
  htmlType?: 'button' | 'submit' | 'reset'
  /**
   * REQUIRED. Icon-only buttons need an accessible name for screen
   * readers — there's no visible text to fall back on.
   */
  'aria-label': string
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------
// Icon stays at 20 px across all utility sizes per the Figma spec. The
// container size grows around it (the padding absorbs the extra space).
const ICON_SIZE = 20

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ButtonUtility = forwardRef<HTMLButtonElement, ButtonUtilityProps>(
  function ButtonUtility(
    {
      type = 'tertiary',
      size = 'sm',
      destructive = false,
      icon,
      loading = false,
      disabled = false,
      htmlType = 'button',
      className,
      'aria-label': ariaLabel,
      'aria-busy': ariaBusy,
      onClick,
      ...rest
    },
    ref,
  ) {
    const isDisabled = Boolean(disabled || loading)

    const classes = [
      styles.root,
      styles[`type-${type}`],
      styles[`size-${size}`],
      destructive ? styles.destructive : null,
      loading ? styles.loading : null,
      className,
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <button
        {...rest}
        ref={ref}
        type={htmlType}
        className={classes}
        disabled={isDisabled}
        aria-busy={loading ? true : ariaBusy}
        aria-label={ariaLabel}
        onClick={isDisabled ? undefined : onClick}
      >
        {loading ? (
          <Loader size={ICON_SIZE} />
        ) : (
          <Icon name={icon} size={ICON_SIZE} color="currentColor" />
        )}
      </button>
    )
  },
)
